<template>
  <layout-header />
  <layout-sidebar />

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="head-icons ms-2">
          <a
            href="javascript:void(0);"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-original-title="Collapse"
            id="collapse-header"
            @click="toggleHeader"
          >
            <i class="ti ti-chevrons-up"></i>
          </a>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <settings-tab />
      <div class="row">
        <general-sidebar />
        <div class="col-xl-9">
          <div class="card">
            <div class="card-body">
              <div class="border-bottom mb-3 pb-3">
                <h4>Security Settings</h4>
                <p class="text-muted mb-0 small">
                  Your login details, password, and account access
                </p>
              </div>

              <div class="border-bottom mb-4 pb-4">
                <h5 class="fw-medium mb-1">My profile</h5>
                <p class="mb-3 text-muted">
                  This is the person currently signed in. Company name, logo, and bank details are under Company details.
                </p>
                <form @submit.prevent="submitProfile">
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">
                        First name <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="profileForm.first_name"
                        type="text"
                        class="form-control"
                        required
                      />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Middle name</label>
                      <input
                        v-model.trim="profileForm.middle_name"
                        type="text"
                        class="form-control"
                      />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">
                        Last name <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="profileForm.last_name"
                        type="text"
                        class="form-control"
                        required
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">
                        Email <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="profileForm.email"
                        type="email"
                        class="form-control"
                        required
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Phone</label>
                      <input
                        v-model.trim="profileForm.phone"
                        type="tel"
                        class="form-control"
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Gender</label>
                      <vue-select
                        :options="['Male', 'Female', 'Other']"
                        placeholder="Select gender"
                        v-model="profileForm.gender"
                      />
                    </div>
                  </div>
                  <div class="d-flex justify-content-end mt-3">
                    <button
                      type="submit"
                      class="btn btn-primary"
                      :disabled="savingProfile"
                    >
                      <span
                        v-if="savingProfile"
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      Save profile
                    </button>
                  </div>
                </form>
              </div>

              <!-- Password -->
              <div
                class="d-flex justify-content-between align-items-center flex-wrap border-bottom mb-3"
              >
                <div class="mb-3">
                  <h5 class="fw-medium mb-1">Password</h5>
                  <div class="d-flex align-items-center flex-wrap">
                    <p class="mb-0 me-2 pe-2 border-end">
                      Set a unique password to protect the account
                    </p>
                    <p class="mb-0 ms-2" v-if="passwordLastChangedLabel">
                      Last changed {{ passwordLastChangedLabel }}
                    </p>
                    <p class="mb-0 ms-2" v-else>
                      Password change date not available
                    </p>
                  </div>
                </div>
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-dark btn-sm"
                    data-bs-toggle="modal"
                    data-bs-target="#change-password"
                  >
                    Change Password
                  </button>
                </div>
              </div>

              <!-- Device management -->
              <div
                class="d-flex justify-content-between align-items-center flex-wrap border-bottom mb-3"
              >
                <div class="mb-3">
                  <h5 class="fw-medium mb-1">Device Management</h5>
                  <p class="mb-0">
                    The devices associated with your account (login sessions)
                  </p>
                </div>
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-dark btn-sm"
                    data-bs-toggle="modal"
                    data-bs-target="#device_management"
                    @click="fetchDevices"
                  >
                    Manage
                  </button>
                </div>
              </div>

              <!-- Account activity -->
              <div
                class="d-flex justify-content-between align-items-center flex-wrap border-bottom mb-3"
              >
                <div class="mb-3">
                  <h5 class="fw-medium mb-1">Account Activity</h5>
                  <p class="mb-0">Recent login activity for your account</p>
                </div>
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-dark btn-sm"
                    data-bs-toggle="modal"
                    data-bs-target="#account_activity"
                    @click="fetchActivity"
                  >
                    View
                  </button>
                </div>
              </div>

              <!-- Deactivate account (purely informational for now) -->
              <div
                class="d-flex justify-content-between align-items-center flex-wrap border-bottom mb-3"
              >
                <div class="mb-3">
                  <h5 class="fw-medium mb-1">Deactivate Account</h5>
                  <p class="mb-0">
                    This will temporarily shut down your account. Please contact
                    support for account reactivation.
                  </p>
                </div>
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-dark btn-sm"
                    data-bs-toggle="modal"
                    data-bs-target="#deactivate-account"
                  >
                    Deactivate
                  </button>
                </div>
              </div>

              <!-- Delete account -->
              <div
                class="d-flex justify-content-between align-items-center flex-wrap row-gap-3"
              >
                <div>
                  <h5 class="fw-medium mb-1">Delete Account</h5>
                  <p class="mb-0">
                    Your account will be permanently deleted. This action cannot
                    be undone.
                  </p>
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-dark btn-sm"
                    data-bs-toggle="modal"
                    data-bs-target="#del-account"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /row -->
    </div>

    <!-- Footer -->
    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->

  <!-- Change Password Modal -->
  <div class="modal fade dq-sec-modal" id="change-password" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered dq-sec-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-lock-password"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Change Password</h5>
              <div class="dq-subtitle">
                Update your password to keep your account secure.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <form @submit.prevent="submitChangePassword">
            <div class="dq-formgrid">
              <div class="dq-field">
                <label class="form-label dq-label">
                  Current Password <span class="text-danger">*</span>
                </label>
                <div class="dq-pass">
                  <input
                    v-model="passwordForm.current_password"
                    :type="show.current ? 'text' : 'password'"
                    class="form-control dq-input"
                    autocomplete="current-password"
                    required
                  />
                  <button
                    class="dq-eye"
                    type="button"
                    @click="show.current = !show.current"
                    :aria-label="
                      show.current ? 'Hide password' : 'Show password'
                    "
                  >
                    <i
                      :class="show.current ? 'ti ti-eye-off' : 'ti ti-eye'"
                    ></i>
                  </button>
                </div>
              </div>

              <div class="dq-field">
                <label class="form-label dq-label">
                  New Password <span class="text-danger">*</span>
                </label>
                <div class="dq-pass">
                  <input
                    v-model="passwordForm.password"
                    :type="show.new ? 'text' : 'password'"
                    class="form-control dq-input"
                    autocomplete="new-password"
                    required
                  />
                  <button
                    class="dq-eye"
                    type="button"
                    @click="show.new = !show.new"
                    :aria-label="show.new ? 'Hide password' : 'Show password'"
                  >
                    <i :class="show.new ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                  </button>
                </div>

                <!-- strength (lightweight, no libs) -->
                <div class="dq-strength" v-if="passwordForm.password">
                  <div class="dq-strength-bar">
                    <span :style="{ width: strengthPct + '%' }"></span>
                  </div>
                  <div class="dq-strength-meta">
                    <span class="dq-strength-label">Strength:</span>
                    <span class="dq-strength-value" :class="strengthClass">{{
                      strengthText
                    }}</span>
                  </div>
                  <div class="dq-hint">
                    Use 8+ chars, mix of letters, numbers and a symbol for best
                    security.
                  </div>
                </div>
              </div>

              <div class="dq-field">
                <label class="form-label dq-label">
                  Confirm New Password <span class="text-danger">*</span>
                </label>
                <div class="dq-pass">
                  <input
                    v-model="passwordForm.password_confirmation"
                    :type="show.confirm ? 'text' : 'password'"
                    class="form-control dq-input"
                    autocomplete="new-password"
                    required
                  />
                  <button
                    class="dq-eye"
                    type="button"
                    @click="show.confirm = !show.confirm"
                    :aria-label="
                      show.confirm ? 'Hide password' : 'Show password'
                    "
                  >
                    <i
                      :class="show.confirm ? 'ti ti-eye-off' : 'ti ti-eye'"
                    ></i>
                  </button>
                </div>

                <div class="dq-match" v-if="passwordForm.password_confirmation">
                  <span
                    class="dq-pill"
                    :class="passwordsMatch ? 'dq-pill-live' : 'dq-pill-warn'"
                  >
                    <i
                      :class="
                        passwordsMatch ? 'ti ti-check' : 'ti ti-alert-triangle'
                      "
                    ></i>
                    {{
                      passwordsMatch
                        ? "Passwords match"
                        : "Passwords do not match"
                    }}
                  </span>
                </div>
              </div>
            </div>

            <div class="dq-actions">
              <button
                type="button"
                class="btn btn-light dq-btn dq-btn-muted"
                data-bs-dismiss="modal"
                :disabled="changingPassword"
              >
                Cancel
              </button>

              <button
                class="btn btn-primary dq-btn dq-btn-primary"
                type="submit"
                :disabled="changingPassword || !passwordsMatch"
              >
                <span
                  v-if="changingPassword"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Save Changes
                <i class="ti ti-check ms-2"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <!-- /Change Password -->

  <!-- Device Management Modal -->
  <div class="modal fade dq-sec-modal" id="device_management" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-wide-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-blue">
              <i class="ti ti-devices"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Device Management</h5>
              <div class="dq-subtitle">
                View active sessions and revoke access from suspicious devices.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-tablewrap">
            <table class="table dq-table mb-0">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Date</th>
                  <th>IP Address</th>
                  <th class="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="devicesLoading">
                  <td colspan="4" class="py-4">
                    <div class="dq-empty">
                      <span
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      Loading devices…
                    </div>
                  </td>
                </tr>

                <tr v-else-if="devices.length === 0">
                  <td colspan="4" class="py-4">
                    <div class="dq-empty">
                      <i class="ti ti-info-circle me-2"></i>
                      No active devices found.
                    </div>
                  </td>
                </tr>

                <tr v-for="d in devices" :key="d.id">
                  <td>
                    <div class="dq-device">
                      <div class="dq-device-ico">
                        <i class="ti ti-device-desktop"></i>
                      </div>
                      <div class="dq-device-meta">
                        <div class="dq-device-name">{{ d.device }}</div>
                        <div class="dq-device-ua" v-if="d.user_agent">
                          {{ d.user_agent }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="dq-date">
                      <div>
                        Created: <b>{{ formatDateTime(d.created_at) }}</b>
                      </div>
                      <div class="text-muted" v-if="d.last_used_at">
                        Last used: {{ formatDateTime(d.last_used_at) }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="dq-pill dq-pill-muted">
                      <i class="ti ti-world"></i> {{ d.ip_address || "-" }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm dq-iconbtn"
                      :disabled="revokingDeviceId === d.id"
                      @click="revokeDevice(d)"
                      title="Revoke device"
                    >
                      <span
                        v-if="revokingDeviceId === d.id"
                        class="spinner-border spinner-border-sm"
                      ></span>
                      <i v-else class="ti ti-trash"></i>
                      <span class="ms-2 d-none d-md-inline">Revoke</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="dq-note mt-3">
            <i class="ti ti-shield-check"></i>
            Tip: Revoke any device you don’t recognize. The user will be logged
            out on that device.
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Device Management -->

  <!-- Activity Management Modal -->
  <div class="modal fade dq-sec-modal" id="account_activity" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-wide-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-purple">
              <i class="ti ti-activity"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Account Activity</h5>
              <div class="dq-subtitle">
                Audit your recent logins and session usage.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-tablewrap">
            <table class="table dq-table mb-0">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Date</th>
                  <th>IP Address</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="activityLoading">
                  <td colspan="4" class="py-4">
                    <div class="dq-empty">
                      <span
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      Loading activity…
                    </div>
                  </td>
                </tr>

                <tr v-else-if="activity.length === 0">
                  <td colspan="4" class="py-4">
                    <div class="dq-empty">
                      <i class="ti ti-info-circle me-2"></i>
                      No activity records found.
                    </div>
                  </td>
                </tr>

                <tr v-for="a in activity" :key="a.id">
                  <td>
                    <div class="dq-device">
                      <div class="dq-device-ico dq-ico-soft">
                        <i class="ti ti-device-mobile"></i>
                      </div>
                      <div class="dq-device-meta">
                        <div class="dq-device-name">{{ a.device }}</div>
                        <div class="dq-device-ua" v-if="a.user_agent">
                          {{ a.user_agent }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="dq-date">
                      <div>
                        Created: <b>{{ formatDateTime(a.created_at) }}</b>
                      </div>
                      <div class="text-muted" v-if="a.last_used_at">
                        Last used: {{ formatDateTime(a.last_used_at) }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="dq-pill dq-pill-muted">
                      <i class="ti ti-world"></i> {{ a.ip_address || "-" }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="dq-pill"
                      :class="
                        a.status === 'Active' ? 'dq-pill-live' : 'dq-pill-muted'
                      "
                    >
                      <i class="ti ti-point-filled"></i> {{ a.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="dq-note mt-3">
            <i class="ti ti-info-circle"></i>
            These records help you detect unusual access patterns.
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Activity Management -->

  <!-- Deactivate Account Modal -->
  <div class="modal fade dq-sec-modal" id="deactivate-account" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered dq-sec-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-gray">
              <i class="ti ti-user-off"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Deactivate Account</h5>
              <div class="dq-subtitle">
                You’ll be logged out on all devices. Reactivation requires
                support.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitDeactivateAccount">
          <div class="modal-body dq-modal-body">
            <div class="dq-warning">
              <i class="ti ti-alert-triangle"></i>
              <div>
                <div class="dq-warning-title">Heads up</div>
                <div class="dq-warning-text">
                  This will disable your account and log you out everywhere.
                </div>
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label dq-label">
                Password <span class="text-danger">*</span>
              </label>
              <div class="dq-pass">
                <input
                  v-model="deactivatePassword"
                  :type="show.deactivate ? 'text' : 'password'"
                  class="form-control dq-input"
                  required
                />
                <button
                  class="dq-eye"
                  type="button"
                  @click="show.deactivate = !show.deactivate"
                >
                  <i
                    :class="show.deactivate ? 'ti ti-eye-off' : 'ti ti-eye'"
                  ></i>
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :disabled="deactivatingAccount"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-dark dq-btn dq-btn-dark"
              :disabled="deactivatingAccount"
            >
              <span
                v-if="deactivatingAccount"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Deactivate Account
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Deactivate Account Modal -->

  <!-- Delete Account Modal -->
  <div class="modal fade dq-sec-modal" id="del-account" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered dq-sec-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Delete Account</h5>
              <div class="dq-subtitle">
                Permanent action. Enter your password to confirm.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitDeleteAccount">
          <div class="modal-body dq-modal-body">
            <div class="dq-danger">
              <i class="ti ti-alert-octagon"></i>
              <div>
                <div class="dq-warning-title">This cannot be undone</div>
                <div class="dq-warning-text">
                  All access will be removed and your account will be
                  permanently deleted.
                </div>
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label dq-label">
                Password <span class="text-danger">*</span>
              </label>
              <div class="dq-pass">
                <input
                  v-model="deletePassword"
                  :type="show.delete ? 'text' : 'password'"
                  class="form-control dq-input"
                  required
                />
                <button
                  class="dq-eye"
                  type="button"
                  @click="show.delete = !show.delete"
                >
                  <i :class="show.delete ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :disabled="deletingAccount"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-danger dq-btn dq-btn-danger"
              :disabled="deletingAccount"
            >
              <span
                v-if="deletingAccount"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Delete Account
              <i class="ti ti-trash ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Delete Account Modal -->
</template>

<script setup>
import { computed, onMounted, ref, reactive, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useUserStore } from "@/stores/userStore";
import { toast } from "@/utils/toast";
import securityService from "@/services/securityService";

const title = "Settings";
const text = "Administration";
const text1 = "Settings";

const router = useRouter();
const authStore = useAuthStore();
const userStore = useUserStore();
const currentUser = computed(() => authStore.currentUser);

const profileForm = reactive({
  id: null,
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  phone: "",
  gender: "",
});
const savingProfile = ref(false);

function populateProfileFromStore() {
  const u = currentUser.value;
  if (!u) return;
  profileForm.id = u.id || null;
  profileForm.first_name = u.first_name || "";
  profileForm.middle_name = u.middle_name || "";
  profileForm.last_name = u.last_name || "";
  profileForm.email = u.email || "";
  profileForm.phone = u.phone || "";
  profileForm.gender = u.gender || "";
}

async function submitProfile() {
  if (!profileForm.id) {
    toast("Unable to determine current user.", "error");
    return;
  }
  if (!profileForm.first_name || !profileForm.last_name || !profileForm.email) {
    toast("First name, last name, and email are required.", "error");
    return;
  }

  savingProfile.value = true;
  try {
    await userStore.updateUser({
      id: profileForm.id,
      first_name: profileForm.first_name,
      middle_name: profileForm.middle_name,
      last_name: profileForm.last_name,
      email: profileForm.email,
      phone: profileForm.phone,
      gender: profileForm.gender,
      fromUser: null,
    });
    await authStore.fetchCurrentUser();
    toast("Your profile was updated");
  } catch {
    // userStore.updateUser already shows the error toast
  } finally {
    savingProfile.value = false;
  }
}

// --- header collapse ---
function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

// --- Password info label ---
function formatDateTime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

const passwordLastChangedLabel = computed(() => {
  const u = currentUser.value;
  if (!u) return "";
  // Prefer password_changed_at; fall back to created_at
  const raw = u.password_changed_at || u.created_at;
  return formatDateTime(raw);
});

// --- Change password form ---
const passwordForm = ref({
  current_password: "",
  password: "",
  password_confirmation: "",
});
const changingPassword = ref(false);

async function submitChangePassword() {
  if (!passwordForm.value.current_password || !passwordForm.value.password) {
    toast("Please fill all password fields.", "error");
    return;
  }
  if (
    passwordForm.value.password !== passwordForm.value.password_confirmation
  ) {
    toast("New password and confirmation do not match.", "error");
    return;
  }

  changingPassword.value = true;
  try {
    await securityService.changePassword({
      current_password: passwordForm.value.current_password,
      password: passwordForm.value.password,
      password_confirmation: passwordForm.value.password_confirmation,
    });

    toast("Password updated successfully");

    // refresh current user so we see updated password_changed_at
    await authStore.fetchCurrentUser();

    // reset form
    passwordForm.value.current_password = "";
    passwordForm.value.password = "";
    passwordForm.value.password_confirmation = "";

    // close modal
    closeModal("change-password");
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      "Failed to change password. Please try again.";
    toast(msg, "error");
  } finally {
    changingPassword.value = false;
  }
}

// --- Devices ---
const devices = ref([]);
const devicesLoading = ref(false);
const revokingDeviceId = ref(null);

async function fetchDevices() {
  devicesLoading.value = true;
  try {
    const res = await securityService.getDevices();
    devices.value = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    const msg = error?.response?.data?.message || "Failed to load devices.";
    toast(msg, "error");
  } finally {
    devicesLoading.value = false;
  }
}

async function revokeDevice(device) {
  if (!device?.id) return;
  revokingDeviceId.value = device.id;
  try {
    const res = await securityService.revokeDevice(device.id);
    devices.value = devices.value.filter((d) => d.id !== device.id);

    toast("Device session revoked");

    if (res?.data?.is_current) {
      // Current session revoked -> log out and redirect
      authStore.logout();
      router.push({ name: "login-index" });
    }
  } catch (error) {
    const msg = error?.response?.data?.message || "Failed to revoke device.";
    toast(msg, "error");
  } finally {
    revokingDeviceId.value = null;
  }
}

// --- Activity ---
const activity = ref([]);
const activityLoading = ref(false);

async function fetchActivity() {
  activityLoading.value = true;
  try {
    const res = await securityService.getActivity();
    activity.value = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    const msg =
      error?.response?.data?.message || "Failed to load account activity.";
    toast(msg, "error");
  } finally {
    activityLoading.value = false;
  }
}

// --- Delete account ---
const deletePassword = ref("");
const deletingAccount = ref(false);

// --- Deactivate Account ---

const deactivatePassword = ref("");
const deactivatingAccount = ref(false);

async function submitDeactivateAccount() {
  if (!deactivatePassword.value) {
    toast("Please enter your current password.", "error");
    return;
  }

  deactivatingAccount.value = true;
  try {
    await securityService.deactivateAccount({
      password: deactivatePassword.value,
    });

    toast(
      "Account deactivated successfully. Please contact support to reactivate."
    );

    deactivatePassword.value = "";

    // Close modal and log out
    closeModal("deactivate-account");
    authStore.logout();
    router.push({ name: "login-index" });
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      "Failed to deactivate account. Please try again.";
    toast(msg, "error");
  } finally {
    deactivatingAccount.value = false;
  }
}

async function submitDeleteAccount() {
  if (!deletePassword.value) {
    toast("Please enter your current password.", "error");
    return;
  }

  deletingAccount.value = true;
  try {
    await securityService.deleteAccount({
      password: deletePassword.value,
    });

    toast("Account deleted successfully");

    deletePassword.value = "";

    // Close modal and log out
    closeModal("del-account");
    authStore.logout();
    router.push({ name: "login-index" });
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      "Failed to delete account. Please try again.";
    toast(msg, "error");
  } finally {
    deletingAccount.value = false;
  }
}

// helper to close bootstrap modals by id
function closeModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.classList.remove("show");
  el.style.display = "none";
  el.setAttribute("aria-hidden", "true");
  el.removeAttribute("aria-modal");
  document.body.classList.remove("modal-open");
  document
    .querySelectorAll(".modal-backdrop")
    .forEach((b) => b.parentNode.removeChild(b));
}

const show = reactive({
  current: false,
  new: false,
  confirm: false,
  deactivate: false,
  delete: false,
});

// computed helpers (safe)
const passwordsMatch = computed(() => {
  if (!passwordForm.value.password_confirmation) return true;
  return passwordForm.value.password === passwordForm.value.password_confirmation;
});

// tiny strength meter (0-100)
const strengthPct = computed(() => {
  const p = passwordForm.value.password || "";
  let score = 0;
  if (p.length >= 8) score += 30;
  if (p.length >= 12) score += 15;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score += 20;
  if (/\d/.test(p)) score += 15;
  if (/[^A-Za-z0-9]/.test(p)) score += 20;
  return Math.min(100, score);
});

const strengthText = computed(() => {
  const v = strengthPct.value;
  if (v >= 80) return "Strong";
  if (v >= 50) return "Good";
  if (v >= 30) return "Weak";
  return "Very weak";
});

const strengthClass = computed(() => {
  const v = strengthPct.value;
  if (v >= 80) return "dq-strong";
  if (v >= 50) return "dq-good";
  return "dq-weak";
});

// optional: pre-load devices/activity once
watch(
  () => currentUser.value,
  () => populateProfileFromStore(),
  { deep: true }
);

onMounted(() => {
  populateProfileFromStore();
  fetchDevices();
  fetchActivity();
});
</script>

<style scoped>
/* dialog sizes */
.dq-sec-dialog {
  max-width: 620px;
}
.dq-wide-dialog {
  max-width: 980px;
}
@media (max-width: 992px) {
  .dq-sec-dialog,
  .dq-wide-dialog {
    max-width: 96%;
  }
}

/* shared modal shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-modal-body {
  padding: 18px 22px 16px;
}
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}

.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* hero icons */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-blue {
  background: rgba(59, 130, 246, 0.14);
  color: rgb(37, 99, 235);
}
.dq-hero-purple {
  background: rgba(168, 85, 247, 0.14);
  color: rgb(147, 51, 234);
}
.dq-hero-gray {
  background: rgba(15, 23, 42, 0.08);
  color: rgba(15, 23, 42, 0.8);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}

/* password group */
.dq-pass {
  position: relative;
}
.dq-eye {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 6px;
  border-radius: 10px;
  color: rgba(15, 23, 42, 0.65);
}
.dq-eye:hover {
  background: rgba(15, 23, 42, 0.06);
  color: rgba(15, 23, 42, 0.85);
}

/* form layout */
.dq-formgrid {
  display: grid;
  gap: 14px;
}
.dq-field {
  display: block;
}

/* actions row */
.dq-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}
.dq-btn-dark {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
}

/* strength meter */
.dq-strength {
  margin-top: 10px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 16px;
  padding: 10px 12px;
  background: rgba(15, 23, 42, 0.02);
}
.dq-strength-bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  overflow: hidden;
}
.dq-strength-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.8);
  transition: width 0.2s ease;
}
.dq-strength-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
  font-size: 0.82rem;
}
.dq-strength-label {
  color: rgba(15, 23, 42, 0.65);
  font-weight: 900;
}
.dq-strength-value {
  font-weight: 950;
}
.dq-weak {
  color: rgb(220, 38, 38);
}
.dq-good {
  color: rgb(37, 99, 235);
}
.dq-strong {
  color: rgb(22, 163, 74);
}
.dq-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 0.78rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-pill-live {
  background: rgba(34, 197, 94, 0.1);
  color: rgb(22, 163, 74);
  border-color: rgba(34, 197, 94, 0.18);
}
.dq-pill-warn {
  background: rgba(245, 158, 11, 0.12);
  color: rgb(180, 83, 9);
  border-color: rgba(245, 158, 11, 0.18);
}
.dq-pill-muted {
  background: rgba(15, 23, 42, 0.05);
  color: rgba(15, 23, 42, 0.75);
}

/* warning blocks */
.dq-warning,
.dq-danger,
.dq-note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-warning i {
  font-size: 18px;
  color: rgb(180, 83, 9);
}
.dq-danger {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.14);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
}
.dq-warning-title {
  font-weight: 950;
  letter-spacing: -0.2px;
}
.dq-warning-text {
  font-size: 0.84rem;
  color: rgba(15, 23, 42, 0.68);
}

/* tables */
.dq-tablewrap {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  overflow: hidden;
  overflow-x: scroll;
}
.dq-table thead th {
  background: rgba(15, 23, 42, 0.03);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 950;
  font-size: 0.82rem;
}
.dq-table td,
.dq-table th {
  vertical-align: middle;
}
.dq-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(15, 23, 42, 0.7);
}

/* device row */
.dq-device {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-device-ico {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  color: rgb(37, 99, 235);
}
.dq-ico-soft {
  background: rgba(168, 85, 247, 0.1);
  color: rgb(147, 51, 234);
}
.dq-device-name {
  font-weight: 950;
}
.dq-device-ua {
  margin-top: 2px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dq-iconbtn {
  border-radius: 12px;
}
</style>


